/**
 * Clean Header System - Minimal JavaScript
 * Simple sticky functionality without complex features
 */

class CleanHeader {
  constructor() {
    this.header = document.getElementById('clean-header');
    this.banner = document.getElementById('scrolling-text-banner');
    this.body = document.body;
    this.activeDropdown = null;
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    this.profileModal = null;
    this.bannerManager = null;
    
    // Mobile menu properties
    this.mobileMenuToggle = null;
    this.mobileNavigation = null;
    this.mobileNavClose = null;
    this.mobileNavBackdrop = null;
    this.mobileMenuOpen = false;
    
    // Search modal properties
    this.searchToggle = null;
    this.searchModal = null;
    this.searchInput = null;
    this.searchClose = null;
    this.searchOverlay = null;
    this.searchModalOpen = false;
    
    if (!this.header) return;
    
    this.init();
  }
  
  init() {
    this.fallbackActive = false;
    this.originalPosition = 0;
    
    this.initBanner();
    this.ensureSticky();
    this.updateHeaderHeight();
    this.initDropdowns();
    this.initMobileMenu();
    this.initSearchModal();
    this.initProfileSystem();
    this.initCartIntegration();
    this.bindEvents();
    
    // REMOVED: Timeout that could cause header disappearing - immediate sticky test instead
    this.performStickyTest();
  }
  
  /**
   * SIMPLIFIED: Minimal sticky positioning validation - trust CSS sticky first
   */
  performStickyTest() {
    if (!this.header) return;
    
    // Phase 1: Basic feature detection only
    const stickySupported = CSS.supports('position', 'sticky') || CSS.supports('position', '-webkit-sticky');
    
    if (!stickySupported) {
      this.enableJavaScriptFallback();
      return;
    }
    
    // Phase 2: Trust CSS sticky is working - only validate if real issues occur
    // Remove complex detection logic that was causing false positives
    this.validateStickyOnDemand();
  }
  
  /**
   * OPTIMIZED: Validate sticky behavior only when user actually scrolls
   */
  validateStickyOnDemand() {
    // Only run validation after user has actually scrolled significantly
    let hasValidated = false;
    
    const validateOnScroll = () => {
      if (hasValidated || window.scrollY < 200) return;
      
      hasValidated = true;
      const rect = this.header.getBoundingClientRect();
      
      // Only trigger fallback if header is clearly broken (not just slightly off)
      if (rect.top < -50 || rect.top > 50) {
        this.enableJavaScriptFallback();
      }
      
      // Remove validation listener after first check
      window.removeEventListener('scroll', validateOnScroll);
    };
    
    // Lightweight scroll listener for one-time validation
    window.addEventListener('scroll', validateOnScroll, { passive: true });
  }
  
  /**
   * Check if sticky positioning context is broken by parent elements
   */
  isStickyContextBroken() {
    let element = this.header.parentElement;
    let level = 0;
    
    while (element && element !== document.body && level < 10) {
      const computedStyle = window.getComputedStyle(element);
      
      // Check for properties that break sticky context
      const overflow = computedStyle.overflow;
      const overflowX = computedStyle.overflowX;
      const overflowY = computedStyle.overflowY;
      const transform = computedStyle.transform;
      const perspective = computedStyle.perspective;
      const filter = computedStyle.filter;
      const clipPath = computedStyle.clipPath;
      
      // These properties can break sticky positioning
      if (
        (overflow !== 'visible' && overflow !== 'auto' && overflow !== 'scroll') ||
        (overflowX !== 'visible' && overflowX !== 'auto' && overflowX !== 'scroll') ||
        (overflowY !== 'visible' && overflowY !== 'auto' && overflowY !== 'scroll') ||
        (transform !== 'none') ||
        (perspective !== 'none') ||
        (filter !== 'none') ||
        (clipPath !== 'none')
      ) {
        return true;
      }
      
      element = element.parentElement;
      level++;
    }
    
    return false;
  }
  
  /**
   * Initialize integrated banner functionality
   */
  initBanner() {
    if (this.banner) {
      this.bannerManager = new ScrollingBannerManager(this.banner, this);
    }
  }

  /**
   * OPTIMIZED: Minimal sticky setup - trust CSS, minimal JavaScript interference
   */
  ensureSticky() {
    if (!this.header) return;
    
    // PHASE 2 OPTIMIZATION: Trust CSS sticky positioning from stylesheet
    // Only add minimal JavaScript enhancements if needed
    this.header.style.willChange = 'transform';
    this.header.style.transform = 'translateZ(0)';
    
    // REMOVED: Aggressive parent container fixing that could cause conflicts
    // CSS has been fixed to handle parent container issues
  }
  
  /**
   * JavaScript fallback for sticky positioning when CSS sticky fails
   */
  enableJavaScriptFallback() {
    this.fallbackActive = true;
    this.originalPosition = this.header.offsetTop;
    
    // Apply fixed positioning with !important to override CSS
    this.header.style.setProperty('position', 'fixed', 'important');
    this.header.style.setProperty('top', '0', 'important');
    this.header.style.setProperty('left', '0', 'important');
    this.header.style.setProperty('width', '100%', 'important');
    this.header.style.setProperty('z-index', '999999', 'important');
    this.header.style.setProperty('transform', 'translateZ(0)', 'important');
    
    // CSS VARIABLES SOLUTION: Update both desktop and mobile offset variables
    const headerHeight = this.header.offsetHeight;
    document.documentElement.style.setProperty('--header-offset', `${headerHeight}px`);
    document.documentElement.style.setProperty('--header-mobile-offset', `${headerHeight}px`);
  }
  
  /**
   * Update CSS custom property with header height
   */
  updateHeaderHeight() {
    if (this.header) {
      const height = this.header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }
  }
  
  /**
   * Smooth CSS variable update helper for header offsets
   */
  updateOffsetVariables(height, smooth = false) {
    if (smooth) {
      // For smooth transitions, use requestAnimationFrame to sync with CSS animations
      requestAnimationFrame(() => {
        document.documentElement.style.setProperty('--header-offset', `${height}px`);
        document.documentElement.style.setProperty('--header-mobile-offset', `${height}px`);
      });
    } else {
      document.documentElement.style.setProperty('--header-offset', `${height}px`);
      document.documentElement.style.setProperty('--header-mobile-offset', `${height}px`);
    }
  }
  
  /**
   * Calculate expected header height - ALWAYS SAME SIZE regardless of scroll state
   */
  getExpectedHeaderHeight(isScrolled) {
    if (!this.header) return 0;
    
    // FIXED: Always return the same total height regardless of banner visibility
    // The header container includes both banner and header areas in a flex column
    // Total height should remain constant - only banner content shows/hides within its allocated space
    const headerContainerHeight = this.header.offsetHeight;
    
    // ALWAYS return exact same height - no changes for scroll state or banner visibility
    return headerContainerHeight;
  }

  /**
   * Handle banner visibility changes (called by banner manager) - FIXED: No container size changes
   */
  handleBannerVisibilityChange(visible) {
    // FIXED: Do NOT change header container dimensions
    // The header container should maintain constant size - only banner content shows/hides within it
    // CSS handles banner visibility with display:none without affecting container height
    
    // NO setTimeout delays that could cause issues
    // NO container size updates that could cause 40px changes
    // CSS variables remain constant since header container size never changes
  }
  
  /**
   * Initialize dropdown functionality
   */
  initDropdowns() {
    if (!this.header) return;
    
    const dropdownItems = this.header.querySelectorAll('.nav-item--has-dropdown');
    
    dropdownItems.forEach(item => {
      const link = item.querySelector('.nav-link--has-dropdown');
      const dropdown = item.querySelector('.nav-dropdown');
      
      if (!link || !dropdown) return;
      
      // Touch device click handling
      if (this.isTouchDevice) {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleDropdown(item, link);
        });
      }
      
      // Keyboard handling
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (this.isTouchDevice) {
            this.toggleDropdown(item, link);
          }
        } else if (e.key === 'Escape') {
          this.closeAllDropdowns();
        }
      });
      
      // Close dropdown when clicking dropdown links
      const dropdownLinks = dropdown.querySelectorAll('.nav-dropdown__link');
      dropdownLinks.forEach(dropdownLink => {
        dropdownLink.addEventListener('click', () => {
          this.closeAllDropdowns();
        });
      });
    });
    
    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.nav-item--has-dropdown')) {
        this.closeAllDropdowns();
      }
    });
  }
  
  /**
   * Toggle dropdown for touch devices
   */
  toggleDropdown(item, link) {
    const isActive = item.classList.contains('nav-item--active');
    
    // Close all other dropdowns first
    this.closeAllDropdowns();
    
    if (!isActive) {
      item.classList.add('nav-item--active');
      link.setAttribute('aria-expanded', 'true');
      this.activeDropdown = item;
    }
  }
  
  /**
   * Close all dropdowns
   */
  closeAllDropdowns() {
    if (!this.header) return;
    
    const activeItems = this.header.querySelectorAll('.nav-item--active');
    activeItems.forEach(item => {
      item.classList.remove('nav-item--active');
      const link = item.querySelector('.nav-link--has-dropdown');
      if (link) {
        link.setAttribute('aria-expanded', 'false');
      }
    });
    
    this.activeDropdown = null;
  }
  
  /**
   * Initialize mobile menu functionality
   */
  initMobileMenu() {
    this.mobileMenuToggle = document.getElementById('mobile-menu-toggle');
    this.mobileNavigation = document.getElementById('mobile-navigation');
    this.mobileNavClose = document.getElementById('mobile-nav-close');
    this.mobileNavBackdrop = this.mobileNavigation?.querySelector('.mobile-nav__backdrop');
    
    if (!this.mobileMenuToggle || !this.mobileNavigation) return;
    
    // Hamburger menu toggle
    this.mobileMenuToggle.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleMobileMenu();
    });
    
    // Close button
    if (this.mobileNavClose) {
      this.mobileNavClose.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeMobileMenu();
      });
    }
    
    // Backdrop click to close
    if (this.mobileNavBackdrop) {
      this.mobileNavBackdrop.addEventListener('click', () => {
        this.closeMobileMenu();
      });
    }
    
    // Initialize mobile dropdowns
    this.initMobileDropdowns();
    
    // Close menu on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.mobileMenuOpen) {
        this.closeMobileMenu();
      }
    });
    
    // Close menu on window resize if mobile menu is open
    window.addEventListener('resize', () => {
      if (this.mobileMenuOpen && window.innerWidth >= 990) {
        this.closeMobileMenu();
      }
    });
  }
  
  /**
   * Initialize mobile navigation dropdowns
   */
  initMobileDropdowns() {
    if (!this.mobileNavigation) return;
    
    const dropdownToggles = this.mobileNavigation.querySelectorAll('.mobile-nav__dropdown-toggle');
    
    dropdownToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleMobileDropdown(toggle);
      });
    });
  }
  
  /**
   * Toggle mobile dropdown
   */
  toggleMobileDropdown(toggle) {
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    const dropdown = toggle.nextElementSibling;
    
    if (!dropdown) return;
    
    // Close all other dropdowns first
    const allToggles = this.mobileNavigation.querySelectorAll('.mobile-nav__dropdown-toggle');
    allToggles.forEach(otherToggle => {
      if (otherToggle !== toggle) {
        otherToggle.setAttribute('aria-expanded', 'false');
        const otherDropdown = otherToggle.nextElementSibling;
        if (otherDropdown) {
          otherDropdown.style.maxHeight = '0';
        }
      }
    });
    
    // Toggle current dropdown
    if (isExpanded) {
      toggle.setAttribute('aria-expanded', 'false');
      dropdown.style.maxHeight = '0';
    } else {
      toggle.setAttribute('aria-expanded', 'true');
      // Calculate the full height needed
      dropdown.style.maxHeight = dropdown.scrollHeight + 'px';
    }
  }
  
  /**
   * Toggle mobile menu visibility
   */
  toggleMobileMenu() {
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }
  
  /**
   * Open mobile menu
   */
  openMobileMenu() {
    if (!this.mobileNavigation || !this.mobileMenuToggle) return;
    
    this.mobileMenuOpen = true;
    
    // Update aria attributes
    this.mobileMenuToggle.setAttribute('aria-expanded', 'true');
    this.mobileNavigation.setAttribute('aria-hidden', 'false');
    
    // Add body scroll lock
    document.body.style.overflow = 'hidden';
    
    // Add focus trap
    this.trapFocusInMobileMenu();
    
    // Focus the close button for accessibility
    if (this.mobileNavClose) {
      setTimeout(() => {
        this.mobileNavClose.focus();
      }, 300);
    }
  }
  
  /**
   * Close mobile menu
   */
  closeMobileMenu() {
    if (!this.mobileNavigation || !this.mobileMenuToggle) return;
    
    this.mobileMenuOpen = false;
    
    // Update aria attributes
    this.mobileMenuToggle.setAttribute('aria-expanded', 'false');
    this.mobileNavigation.setAttribute('aria-hidden', 'true');
    
    // Remove body scroll lock
    document.body.style.overflow = '';
    
    // Remove focus trap
    this.removeFocusTrap();
    
    // Close all mobile dropdowns
    const allToggles = this.mobileNavigation.querySelectorAll('.mobile-nav__dropdown-toggle');
    allToggles.forEach(toggle => {
      toggle.setAttribute('aria-expanded', 'false');
      const dropdown = toggle.nextElementSibling;
      if (dropdown) {
        dropdown.style.maxHeight = '0';
      }
    });
    
    // Return focus to hamburger button
    this.mobileMenuToggle.focus();
  }
  
  /**
   * Trap focus within mobile menu for accessibility
   */
  trapFocusInMobileMenu() {
    if (!this.mobileNavigation) return;
    
    const focusableElements = this.mobileNavigation.querySelectorAll(
      'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    this.focusTrapHandler = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };
    
    document.addEventListener('keydown', this.focusTrapHandler);
  }
  
  /**
   * Remove focus trap
   */
  removeFocusTrap() {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }

  /**
   * Initialize search modal functionality
   */
  initSearchModal() {
    this.searchToggle = document.getElementById('search-toggle');
    this.searchModal = document.getElementById('search-modal');
    this.searchInput = document.getElementById('search-modal-input');
    this.searchClose = this.searchModal?.querySelector('.search-modal__close');
    this.searchOverlay = this.searchModal?.querySelector('.search-modal__overlay');
    
    if (!this.searchToggle || !this.searchModal) return;
    
    // Search button click
    this.searchToggle.addEventListener('click', (e) => {
      e.preventDefault();
      this.openSearchModal();
    });
    
    // Close button click
    if (this.searchClose) {
      this.searchClose.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeSearchModal();
      });
    }
    
    // Overlay click to close
    if (this.searchOverlay) {
      this.searchOverlay.addEventListener('click', () => {
        this.closeSearchModal();
      });
    }
    
    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.searchModalOpen) {
        this.closeSearchModal();
      }
    });
  }
  
  /**
   * Open search modal
   */
  openSearchModal() {
    if (!this.searchModal || !this.searchToggle) return;
    
    this.searchModalOpen = true;
    
    // Update aria attributes
    this.searchModal.setAttribute('aria-hidden', 'false');
    this.searchToggle.setAttribute('aria-expanded', 'true');
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    
    // Focus input after modal opens
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.focus();
      }
    }, 100);
  }
  
  /**
   * Close search modal
   */
  closeSearchModal() {
    if (!this.searchModal || !this.searchToggle) return;
    
    this.searchModalOpen = false;
    
    // Update aria attributes
    this.searchModal.setAttribute('aria-hidden', 'true');
    this.searchToggle.setAttribute('aria-expanded', 'false');
    
    // Restore body scroll
    document.body.style.overflow = '';
    
    // Clear input
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    
    // Return focus to search button
    this.searchToggle.focus();
  }

  /**
   * Initialize profile system (login modal/dropdown)
   */
  initProfileSystem() {
    const profileButton = this.header.querySelector('.profile-button');
    if (!profileButton) return;

    profileButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleProfileClick();
    });
  }

  /**
   * Handle profile button click
   */
  handleProfileClick() {
    // Check if customer is logged in via Shopify's customer object
    if (window.Shopify && window.Shopify.customer) {
      // Customer is logged in - redirect to account
      window.location.href = '/account';
    } else {
      // Customer not logged in - redirect to login
      window.location.href = '/account/login';
    }
  }

  /**
   * Initialize cart integration
   */
  initCartIntegration() {
    // Cart drawer integration is handled by the existing CartDrawer class
    // which looks for #cart-icon-bubble - this is already set in our HTML
    
    // Override cart button click behavior to check for empty cart
    this.overrideCartButtonClick();
    
    // Update cart count when cart changes
    this.updateCartCount();
    
    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      this.updateCartCount();
    });
  }

  /**
   * Override cart button click to handle empty cart
   */
  overrideCartButtonClick() {
    const cartButton = this.header.querySelector('#cart-icon-bubble');
    if (!cartButton) return;

    // Use capture phase to intercept before CartDrawer's listener
    cartButton.addEventListener('click', (event) => {
      // Synchronous check using window.cartState
      if (window.cartState && window.cartState.isEmpty) {
        // Prevent drawer from opening immediately
        event.preventDefault();
        event.stopImmediatePropagation();
        
        // Show toast message instead
        this.showEmptyCartToast();
        return false;
      }
      // If cart has items, let the normal drawer opening process continue
    }, true); // Use capture phase to intercept before CartDrawer's listener
  }

  /**
   * Show empty cart tooltip message
   */
  showEmptyCartToast() {
    const cartButton = this.header.querySelector('#cart-icon-bubble');
    if (!cartButton) return;
    
    // Get localized message
    const message = window.cartStrings?.empty_cart_message || 'Sepetinize ürün ekleyin';
    
    // Add tooltip and disabled styling
    cartButton.title = message;
    cartButton.style.cursor = 'not-allowed';
    cartButton.classList.add('cart-empty-disabled');
    
    // Remove tooltip after 3 seconds
    setTimeout(() => {
      cartButton.title = '';
      cartButton.style.cursor = '';
      cartButton.classList.remove('cart-empty-disabled');
    }, 3000);
  }

  /**
   * Update cart count bubble and handle shake animation
   */
  updateCartCount() {
    const cartButton = this.header.querySelector('#cart-icon-bubble');
    if (!cartButton) return;

    // Get cart data from Shopify
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Update global cart state
        this.updateGlobalCartState(cart);
        
        const countBubble = cartButton.querySelector('.cart-count-bubble');
        const cartButtonContent = cartButton.querySelector('.cart-button-content');
        
        if (cart.item_count > 0) {
          // Add cart-has-items class for shake animation
          cartButton.classList.add('cart-has-items');
          
          if (!countBubble) {
            // Create count bubble if it doesn't exist
            const bubble = document.createElement('span');
            bubble.className = 'cart-count-bubble';
            bubble.innerHTML = `
              <span aria-hidden="true">${cart.item_count}</span>
              <span class="visually-hidden">${cart.item_count} items in cart</span>
            `;
            cartButton.appendChild(bubble);
          } else {
            // Update existing bubble
            countBubble.querySelector('[aria-hidden="true"]').textContent = cart.item_count;
            countBubble.querySelector('.visually-hidden').textContent = `${cart.item_count} items in cart`;
          }
          
          // Initialize shake animation scheduling
          this.initCartShakeAnimation(cartButton);
        } else {
          // Remove bubble and shake animation class if cart is empty
          cartButton.classList.remove('cart-has-items');
          this.clearCartShakeAnimation(cartButton);
          
          if (countBubble) {
            countBubble.remove();
          }
        }
      })
      .catch(error => {
        console.warn('Failed to update cart count:', error);
      });
  }

  /**
   * Update global cart state for synchronous access
   */
  updateGlobalCartState(cart) {
    if (window.cartState) {
      window.cartState.item_count = cart.item_count;
      window.cartState.isEmpty = cart.item_count === 0;
      window.cartState.lastUpdated = Date.now();
    }
  }

  /**
   * Initialize cart shake animation for filled cart
   */
  initCartShakeAnimation(cartButton) {
    if (cartButton.hasAttribute('data-shake-initialized')) return;
    
    cartButton.setAttribute('data-shake-initialized', 'true');
    
    // Schedule periodic shake animations (every 4 seconds)
    const scheduleShake = () => {
      if (!cartButton.classList.contains('cart-has-items')) return;
      
      // Trigger shake animation
      const cartIcon = cartButton.querySelector('.cart-icon-custom');
      if (cartIcon) {
        cartIcon.style.animation = 'none'; // Reset animation
        cartIcon.offsetHeight; // Force reflow
        cartIcon.style.animation = 'cart-shake 0.5s ease-in-out';
        
        // Clear animation after completion
        setTimeout(() => {
          if (cartIcon) cartIcon.style.animation = '';
        }, 500);
      }
      
      // Schedule next shake
      cartButton.shakeTimeout = setTimeout(scheduleShake, 4000);
    };
    
    // Start first shake after 3 seconds
    cartButton.initialShakeTimeout = setTimeout(() => {
      scheduleShake();
    }, 3000);
  }

  /**
   * Clear cart shake animation
   */
  clearCartShakeAnimation(cartButton) {
    cartButton.removeAttribute('data-shake-initialized');
    
    // Clear timeouts
    if (cartButton.initialShakeTimeout) {
      clearTimeout(cartButton.initialShakeTimeout);
      delete cartButton.initialShakeTimeout;
    }
    
    if (cartButton.shakeTimeout) {
      clearTimeout(cartButton.shakeTimeout);
      delete cartButton.shakeTimeout;
    }
    
    // Reset animation
    const cartIcon = cartButton.querySelector('.cart-icon-custom');
    if (cartIcon) {
      cartIcon.style.animation = '';
    }
  }
  
  /**
   * Bind event listeners
   */
  bindEvents() {
    // Prevent dragging on all header images, SVGs, anchor links, and buttons
    const draggableElements = this.header.querySelectorAll('img, svg, a, button');
    draggableElements.forEach(element => {
      element.setAttribute('draggable', 'false');
      element.addEventListener('dragstart', (e) => {
        e.preventDefault();
        return false;
      });
    });
    
    // Update header height on resize
    window.addEventListener('resize', () => {
      this.updateHeaderHeight();
      this.closeAllDropdowns(); // Close dropdowns on resize
    }, { passive: true });
    
    // Handle theme editor section reloads
    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId.includes('header')) {
        setTimeout(() => {
          this.updateHeaderHeight();
          this.initDropdowns(); // Reinitialize dropdowns
        }, 100);
      }
    });
    
    // Enhanced sticky positioning enforcement
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.enforceSticky();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
    
    // Simple logo hover effect
    const logoLink = this.header.querySelector('.logo-link');
    if (logoLink) {
      logoLink.addEventListener('mouseenter', () => {
        logoLink.style.transform = 'scale(1.02)';
      });
      
      logoLink.addEventListener('mouseleave', () => {
        logoLink.style.transform = 'scale(1)';
      });
    }
  }
  
  /**
   * SIMPLIFIED: Basic sticky positioning enforcement without complex animations
   */
  enforceSticky() {
    if (!this.header) return;
    
    const scrollY = window.scrollY;
    const SCROLL_THRESHOLD = 50;
    const isScrolled = scrollY > SCROLL_THRESHOLD;
    const wasScrolled = this.header.classList.contains('clean-header--scrolled');
    
    // Simple scroll state management - no complex transition monitoring
    if (isScrolled && !wasScrolled) {
      this.header.classList.add('clean-header--scrolled');
      if (this.bannerManager) {
        this.bannerManager.hideBannerOnScroll();
      }
    } else if (!isScrolled && wasScrolled) {
      this.header.classList.remove('clean-header--scrolled');
      if (this.bannerManager) {
        this.bannerManager.showBannerOnScrollTop();
      }
    }
    
    // Basic sticky position maintenance
    const computedStyle = window.getComputedStyle(this.header);
    if (this.fallbackActive) {
      if (computedStyle.position !== 'fixed') {
        this.header.style.setProperty('position', 'fixed', 'important');
        this.header.style.setProperty('top', '0', 'important');
        this.header.style.setProperty('z-index', '999999', 'important');
      }
    } else {
      if (computedStyle.position !== 'sticky' && computedStyle.position !== '-webkit-sticky') {
        this.header.style.position = 'sticky';
        this.header.style.top = '0';
        this.header.style.zIndex = '999999';
      }
    }
  }
  
  // REMOVED: Complex transition monitoring and easing calculations
  // These functions were causing jarring animations and conflicts
  
  /**
   * Get current header height
   */
  getHeaderHeight() {
    return this.header ? this.header.offsetHeight : 0;
  }
  
  /**
   * Clean up event listeners
   */
  destroy() {
    // Close all dropdowns
    this.closeAllDropdowns();
    
    // Close mobile menu if open
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
    }
    
    // Remove focus trap
    this.removeFocusTrap();
    
    // Remove body scroll lock
    document.body.style.overflow = '';
    
    // Clear transition timeout if active
    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout);
      this.transitionTimeout = null;
    }
    
    // Remove header height CSS variables
    document.documentElement.style.removeProperty('--header-height');
    document.documentElement.style.removeProperty('--header-offset');
    document.documentElement.style.removeProperty('--header-mobile-offset');
  }
}

/**
 * SIMPLIFIED: ScrollingBannerManager - Basic Banner Functionality
 * Simplified banner system without complex animations
 */
class ScrollingBannerManager {
  constructor(bannerElement, headerInstance) {
    this.banner = bannerElement;
    this.header = headerInstance;
    this.isVisible = true;
    
    this.init();
  }
  
  init() {
    // Simple banner setup - let CSS handle all animations
    this.showBanner();
    this.bindBasicEvents();
  }
  
  /**
   * SIMPLIFIED: Basic event binding without complex animation monitoring
   */
  bindBasicEvents() {
    // Prevent dragging on banner elements
    const draggableElements = this.banner.querySelectorAll('*');
    draggableElements.forEach(element => {
      element.setAttribute('draggable', 'false');
      element.addEventListener('dragstart', (e) => {
        e.preventDefault();
        return false;
      });
    });
  }
  
  /**
   * SIMPLIFIED: Show banner 
   */
  showBanner() {
    this.banner.style.display = '';
    this.isVisible = true;
  }
  
  /**
   * BANNER ALWAYS VISIBLE: Keep banner visible on scroll (no hiding)
   */
  hideBannerOnScroll() {
    // BANNER ALWAYS STICKY: Do nothing - banner stays visible
    // isVisible remains true, no CSS changes, no header notifications needed
    return;
  }
  
  /**
   * BANNER ALWAYS VISIBLE: Banner is always shown (no toggle needed)
   */
  showBannerOnScrollTop() {
    // BANNER ALWAYS STICKY: Do nothing - banner is already always visible
    // isVisible remains true, no changes needed
    return;
  }
  
  /**
   * SIMPLIFIED: Basic cleanup - no complex animation systems to clean up
   */
  cleanup() {
    // Simple cleanup - no complex timers or observers
  }
}

// Global header controller instance
window.cleanHeader = null;

// Initialize when DOM is ready
function initCleanHeader() {
  // Clean up existing instance
  if (window.cleanHeader) {
    window.cleanHeader.destroy();
  }
  
  // Create new instance
  window.cleanHeader = new CleanHeader();
}

// Initialize immediately if DOM is ready, otherwise wait
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCleanHeader);
} else {
  initCleanHeader();
}

// Handle Shopify theme editor updates
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId.includes('header')) {
    setTimeout(initCleanHeader, 100);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (event.detail.sectionId.includes('header') && window.cleanHeader) {
    window.cleanHeader.destroy();
    window.cleanHeader = null;
  }
});

// Export for testing and external access
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CleanHeader, ScrollingBannerManager };
}
